import { z } from 'zod'

/**
 * The shape the api attaches at read time from a stored asset id.
 *
 * `width`/`height` are the intrinsic pixel dimensions, passed straight to the `<img>` so the
 * browser reserves layout space — that is the entire cumulative-layout-shift story. `srcset` is a
 * pre-built descriptor string, so the client never does url arithmetic.
 *
 * All three are nullish on purpose, so three different producers satisfy the shape without
 * ceremony: the full read-time resolver, a dimensionless source such as an svg or a pdf, and the
 * admin media picker's `{ url, altText }`. Renderers treat missing and null identically.
 */
export const resolvedMediaSchema = z.object({
    url: z.string(),
    altText: z.string().nullable(),
    width: z.number().int().positive().nullish(),
    height: z.number().int().positive().nullish(),
    srcset: z.string().nullish(),
})

export type ResolvedMedia = z.infer<typeof resolvedMediaSchema>

/**
 * The naming convention is a contract: a stored `fooMediaId` resolves to a sibling `fooMedia`, and
 * a bare `mediaId` to `media`. Uniformity is what lets the strip-before-save pass and the
 * where-used collectors be written mechanically rather than field by field.
 *
 * The table is `CmsAsset` but the field pairs say "media" — an asset is the row, media is the slot
 * a block reserves for one.
 */
const ID_SUFFIX = 'Id'

/**
 * A media slot is any key ending in `Id` whose stem mentions media: `mediaId`, `mediaMobileId`,
 * `imageMediaMobileId`, `authorPhotoMediaId`.
 *
 * Matching on the stem rather than on a `MediaId` suffix is load-bearing. A suffix test misses
 * every key that qualifies the slot *after* the word — `mediaMobileId` ends in `MobileId` — so the
 * mobile crop of every block was collected by neither the read-time resolver nor the delete guard,
 * and art direction silently never happened. The test is case-insensitive on the stem only, which
 * is what keeps `assetId` and `pageId` out of it.
 */
export const isResolvedMediaIdKey = (key: string): boolean =>
    key.endsWith(ID_SUFFIX) && key.slice(0, -ID_SUFFIX.length).toLowerCase().includes('media')

export const toResolvedMediaKey = (idKey: string): string => idKey.slice(0, -ID_SUFFIX.length)
